import { useLocation, useNavigate } from 'react-router-dom';

/**
 * A page's typed search, kept in the history entry's state and never in its
 * address (Arthur, 16 October). App Service logs every page address it serves,
 * and a page's address used to ride along as the Referer of every API call it
 * made, so `?q=` put a client's name in Log Analytics twice over. Back and
 * forward still bring the search back, because each entry keeps its own state;
 * a reload or a copied link starts with the box empty, which is the price.
 *
 * `drop` names address keys to clear in the same step (a new search starts at
 * the first page).
 */
export function usePageSearch(): [string, (text: string, drop?: string[]) => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state ?? null) as { search?: string } | null;
  const text = typeof state?.search === 'string' ? state.search : '';
  const set = (next: string, drop: string[] = []) => {
    const params = new URLSearchParams(location.search);
    for (const key of drop) params.delete(key);
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true, state: { ...(state ?? {}), search: next || undefined } },
    );
  };
  return [text, set];
}

/** For changing a page's filters without losing its typed search: pass as the navigation's options. */
export function useKeepState(): { state: unknown } {
  return { state: useLocation().state };
}
